/** @format */

import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getVendorSettings, setVendorData } from "../../services/helper";
import http from "../../services/http";
import { useDispatch } from "react-redux";
import { setPosItems } from "../../ReduxToolkit/Reducers/CartReducer";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";

const POSAddonModal = ({
  modal,
  toggleModal,
  product,
  addonQuantities,
  setAddonQuantities,
  handleAddToCartWithAddons,
}) => {
  const [subtotal, setSubtotal] = useState(parseFloat(product.price) || 0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [addonOrder, setAddonOrder] = useState(product.addons || []);
  const [draggedAddonIndex, setDraggedAddonIndex] = useState(null);
  const searchInputRef = React.useRef(null);
  const dispatch = useDispatch();

  // Update addon order when product changes
  useEffect(() => {
    setAddonOrder(product.addons || []);
  }, [product.addons]);

  // Check if any options are selected
  const hasSelections = Object.keys(addonQuantities).some((addonId) => {
    const selectedOptions = addonQuantities[addonId] || {};
    return Object.keys(selectedOptions).some(
      (optionId) => selectedOptions[optionId]
    );
  });

  // Handle addon changes for select, radio, checkbox
  const handleChange = (addon, option, isSelected) => {
    const addonId = addon.id;
    const optionId = option.id;

    setAddonQuantities((prev) => {
      const updated = { ...prev };

      if (addon.is_multiple) {
        // ✅ Allow multiple options
        updated[addonId] = {
          ...(updated[addonId] || {}),
          [optionId]: isSelected ? 0 : 1,
        };
      } else {
        // ✅ Only one selection allowed
        if (isSelected) {
          // If deselecting, remove the selection
          delete updated[addonId];
        } else {
          // If selecting, set the new selection
          updated[addonId] = { [optionId]: 1 };
        }
      }

      // Clean up empty addon entries
      if (updated[addonId] && Object.keys(updated[addonId]).length === 0) {
        delete updated[addonId];
      }

      return updated;
    });
  };

  // Calculate subtotal based on selected addons
  useEffect(() => {
    let total = parseFloat(product.price) || 0;

    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      Object.keys(selectedOptions).forEach((optionId) => {
        const option = addon.options.find((o) => o.id === optionId);
        if (option && selectedOptions[optionId]) {
          total += parseFloat(option.price || 0);
        }
      });
    });

    setSubtotal(total);
  }, [addonQuantities, product, addonOrder]);

  // Validate required fields (flavor, size, or addon)
  const validateRequiredFields = useCallback(() => {
    for (const addon of addonOrder) {
      if (addon.is_required) {
        const selectedOptions = addonQuantities[addon.id] || {};
        const hasSelection = Object.keys(selectedOptions).some(
          (optionId) => selectedOptions[optionId]
        );
        if (!hasSelection) {
          toast.error(`Please select a required option for "${addon.title}".`);
          return false;
        }
      }
    }
    return true;
  }, [addonOrder, addonQuantities]);

  // Prepare data for adding selected addons to the cart
  const prepareAddonData = useCallback(() => {
    const formattedAddons = {};

    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      const selected = Object.keys(selectedOptions)
        .filter((optionId) => selectedOptions[optionId])
        .map((optionId) => {
          const option = addon.options.find((o) => o.id === optionId);
          return option
            ? `${option.option}${
                option.price
                  ? ` (+$${parseFloat(option.price).toFixed(2)})`
                  : ""
              }`
            : null;
        })
        .filter(Boolean)
        .join(", ");

      if (selected) {
        formattedAddons[addon.title] = selected;
      }
    });

    return JSON.stringify(formattedAddons);
  }, [addonOrder, addonQuantities]);

  // Handle Add to Cart with validation
  const handleAddToCart = useCallback(() => {
    if (!validateRequiredFields()) {
      return;
    }

    const selectedAddons = prepareAddonData();
    handleAddToCartWithAddons(selectedAddons, subtotal);
  }, [
    validateRequiredFields,
    prepareAddonData,
    handleAddToCartWithAddons,
    subtotal,
  ]);

  const isAddonVisible = (addon, addonQuantities) => {
    const conditions = addon.visibility_condition;

    // If no condition, empty condition, or empty field/value, default to visible
    if (!conditions || 
        (Array.isArray(conditions) && conditions.length === 0) ||
        (conditions.field === '' && conditions.value === '') ||
        (!conditions.field && !conditions.value)) {
      return true;
    }

    // Handle single condition object
    if (!Array.isArray(conditions)) {
      const { field, value } = conditions;
      if (!field || !value) return true;
      
      // Look for any addon selection that matches the condition
      for (const selectedAddonId in addonQuantities) {
        const selectedOptions = addonQuantities[selectedAddonId];
        if (selectedOptions && selectedOptions[value]) {
          return true;
        }
      }
      return false;
    }

    // Handle array of conditions - all must be satisfied
    return conditions.every((cond) => {
      const { field, value } = cond;
      if (!field || !value) return true;

      // Look for any addon selection that matches the condition
      for (const selectedAddonId in addonQuantities) {
        const selectedOptions = addonQuantities[selectedAddonId];
        if (selectedOptions && selectedOptions[value]) {
          return true;
        }
      }
      return false;
    });
  };

  // Update pos_items addon order in local storage
  const updatePosItemsAddonOrder = async (productId, newAddonOrder) => {
    try {
      const itemsString = getVendorSettings("pos_items");
      let pos_items = [];

      try {
        pos_items = JSON.parse(itemsString) || [];
      } catch (err) {
        console.warn("Failed to parse pos_items:", err);
        return;
      }

      // Find and update the product's addon order
      const productIndex = pos_items.findIndex((item) => item.id === productId);
      if (productIndex !== -1) {
        pos_items[productIndex].addons = newAddonOrder;

        // Save to localStorage, Redux state, and vendor settings
        localStorage.setItem("pos_items", JSON.stringify(pos_items));
        dispatch(setPosItems(pos_items));
        await http.saveVendorData({ pos_items: JSON.stringify(pos_items) });
        setVendorData({ pos_items: JSON.stringify(pos_items) });
      }
    } catch (error) {
      console.error("Failed to update pos_items addon order:", error);
    }
  };

  // Handle keyboard shortcut Alt+A to add to cart
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key.toLowerCase() === "a" && modal && subtotal > 0) {
        e.preventDefault();
        handleAddToCart();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal, subtotal, handleAddToCart]);

  return (
    <Modal
      isOpen={modal}
      toggle={toggleModal}
      size="lg"
      onOpened={() => {
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 50);
      }}
    >
      <ModalHeader toggle={toggleModal}>{product.name}</ModalHeader>
      <ModalBody>
        <input type="hidden" name="add-to-cart" value={product.id} />
        <input type="hidden" name="tbo_addons" value={prepareAddonData()} />
        <input type="hidden" name="total_price" value={subtotal.toFixed(2)} />

        <Row>
          <Col md="8">
            <p>
              <strong>Base Price:</strong> $
              {parseFloat(product.price || 0).toFixed(2)}
            </p>

            <div className="mb-3 position-relative">
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-search"></i>
                </InputGroupText>
                <Input
                  innerRef={searchInputRef}
                  type="text"
                  placeholder="Search addon options..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(e.target.value.length > 0);
                    setSelectedIndex(-1);
                  }}
                  onFocus={() => setShowDropdown(searchQuery.length > 0)}
                  onKeyDown={(e) => {
                    const filteredResults = addonOrder
                      .filter((addon) => isAddonVisible(addon, addonQuantities))
                      .flatMap((addon) =>
                        addon.options
                          .filter((option) =>
                            option.option
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())
                          )
                          .map((option) => ({
                            ...option,
                            addonTitle: addon.title,
                            addon,
                          }))
                      );

                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setSelectedIndex((prev) =>
                        prev < filteredResults.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                    } else if (e.key === "Enter" && selectedIndex >= 0) {
                      e.preventDefault();
                      const selected = filteredResults[selectedIndex];
                      handleChange(
                        selected.addon,
                        {
                          id: selected.id,
                          option: selected.option,
                          price: selected.price,
                        },
                        false
                      );
                      setSearchQuery("");
                      setShowDropdown(false);
                      setSelectedIndex(-1);
                    } else if (e.key === "Escape") {
                      setShowDropdown(false);
                      setSelectedIndex(-1);
                    } else if (
                      e.altKey &&
                      e.key.toLowerCase() === "a" &&
                      subtotal > 0
                    ) {
                      e.preventDefault();
                      handleAddToCart();
                    }
                  }}
                />
              </InputGroup>

              {showDropdown && searchQuery && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-sm"
                  style={{
                    zIndex: 1050,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {addonOrder
                    .filter((addon) => isAddonVisible(addon, addonQuantities))
                    .flatMap((addon) =>
                      addon.options
                        .filter((option) =>
                          option.option
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        )
                        .map((option) => ({
                          ...option,
                          addonTitle: addon.title,
                          addon,
                        }))
                    )
                    .map(({ option, price, addonTitle, addon, id }, index) => (
                      <div
                        key={`${addon.id}-${id}-${index}`}
                        className="p-2 border-bottom cursor-pointer"
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === index ? "#e6f7f5" : "white",
                          color: "black",
                          border:
                            selectedIndex === index
                              ? "2px solid #20c997"
                              : "none",
                          borderRadius: selectedIndex === index ? "4px" : "0",
                        }}
                        onClick={() => {
                          handleChange(addon, { id, option, price }, false);
                          setSearchQuery("");
                          setShowDropdown(false);
                          setSelectedIndex(-1);
                        }}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <strong>{addonTitle}</strong> &gt; {option}
                        {price && ` (+$${parseFloat(price).toFixed(2)})`}
                      </div>
                    ))}
                  {addonOrder
                    .filter((addon) => isAddonVisible(addon, addonQuantities))
                    .flatMap((addon) =>
                      addon.options.filter((option) =>
                        option.option
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      )
                    ).length === 0 && (
                    <div className="p-2 text-muted">No options found</div>
                  )}
                </div>
              )}
            </div>

            {addonOrder.map((addon, addonIndex) => {
              if (!isAddonVisible(addon, addonQuantities)) return null;

              return (
                <div
                  key={`addon-${addon.id}-${addonIndex}`}
                  className="mb-3"
                  draggable
                  onDragStart={(e) => {
                    setDraggedAddonIndex(addonIndex);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (
                      draggedAddonIndex === null ||
                      draggedAddonIndex === addonIndex
                    )
                      return;

                    const newOrder = [...addonOrder];
                    const [draggedAddon] = newOrder.splice(
                      draggedAddonIndex,
                      1
                    );
                    newOrder.splice(addonIndex, 0, draggedAddon);

                    setAddonOrder(newOrder);
                    setDraggedAddonIndex(null);

                    // Update pos_items in local storage
                    updatePosItemsAddonOrder(product.id, newOrder);
                  }}
                  style={{
                    opacity: draggedAddonIndex === addonIndex ? 0.5 : 1,
                    border:
                      draggedAddonIndex === addonIndex
                        ? "2px dashed #007bff"
                        : "1px solid transparent",
                    borderRadius: "4px",
                    padding: "8px",
                    cursor: "move",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 d-flex align-items-center">
                        <i
                          className="icofont icofont-drag me-2 text-muted"
                          style={{ cursor: "grab" }}
                        ></i>
                        {addon.title}{" "}
                        {addon.is_required && (
                          <span style={{ color: "red" }}>*</span>
                        )}
                      </h6>
                      {addonQuantities[addon.id] &&
                        Object.keys(addonQuantities[addon.id]).length > 0 && (
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            className="m-2"
                            onClick={() => {
                              setAddonQuantities((prev) => {
                                const updated = { ...prev };
                                delete updated[addon.id];
                                return updated;
                              });
                            }}
                          >
                            Clear
                          </Button>
                        )}
                    </div>
                  </div>

                  {addon.is_multiple ? (
                    // ✅ Render multiple selections (checkbox-like grid)
                    <Row>
                      {addon.options.map((option, optionIndex) => {
                        const selectedOptions = addonQuantities[addon.id] || {};
                        const isSelected = selectedOptions[option.id] === 1;
                        const uniqueKey = `${addon.id}-${option.id}-${optionIndex}-multi`;

                        return (
                          <Col md="4" key={uniqueKey} className="mb-2">
                            <div
                              onClick={() =>
                                handleChange(addon, option, isSelected)
                              }
                              className="p-2 rounded text-center border"
                              style={{
                                cursor: "pointer",
                                backgroundColor: isSelected
                                  ? "#198754"
                                  : option.color || "#f8f9fa",
                                color: isSelected ? "#fff" : "#000",
                                borderColor: isSelected ? "#198754" : "#ced4da",
                                fontWeight: "bold",
                              }}
                            >
                              {option.option}{" "}
                              {option.price
                                ? `(+${parseFloat(option.price).toFixed(2)})`
                                : ""}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  ) : addon.type === "select" ? (
                    // ✅ Single select dropdown
                    <select
                      className="form-control"
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedOption = addon.options.find(
                          (o) => o.id === selectedId
                        );
                        handleChange(addon, selectedOption, false);
                      }}
                    >
                      <option value="">-- Select --</option>
                      {addon.options.map((option, optionIndex) => {
                        const uniqueKey = `${addon.id}-${option.id}-${optionIndex}-select`;
                        return (
                          <option key={uniqueKey} value={option.id}>
                            {option.option} (+${option.price})
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    // ✅ Single-choice grid (radio style)
                    <Row>
                      {addon.options.map((option, optionIndex) => {
                        const isSelected =
                          !!addonQuantities[addon.id]?.[option.id];
                        const uniqueKey = `${addon.id}-${option.id}-${optionIndex}-single`;
                        return (
                          <Col md="4" key={uniqueKey} className="mb-2">
                            <div
                              onClick={() =>
                                handleChange(addon, option, isSelected)
                              }
                              className="p-2 rounded text-center border"
                              style={{
                                cursor: "pointer",
                                backgroundColor: isSelected
                                  ? "#198754"
                                  : option.color || "#f8f9fa",
                                color: isSelected ? "#fff" : "#000",
                                borderColor: isSelected ? "#198754" : "#ced4da",
                                fontWeight: "bold",
                              }}
                            >
                              {option.option}{" "}
                              {option.price
                                ? `(+${parseFloat(option.price).toFixed(2)})`
                                : ""}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              );
            })}
          </Col>
          {hasSelections && (
            <Col md="4">
              <Card className="border-teal">
                <CardHeader className="bg-teal" style={{ color: "black" }}>
                  <strong>Selected Options</strong>
                </CardHeader>
                <CardBody style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {Object.keys(addonQuantities).map((addonId) => {
                    // Try to find addon in both addonOrder and original product.addons
                    let addon = addonOrder.find((a) => a.id === addonId);
                    if (!addon) {
                      addon = product.addons?.find((a) => a.id === addonId);
                    }
                    if (!addon) return null;

                    const selectedOptions = addonQuantities[addonId];
                    if (!selectedOptions) return null;
                    
                    const selectedOptionDetails = Object.keys(selectedOptions)
                      .filter((optionId) => selectedOptions[optionId])
                      .map((optionId) => {
                        const option = addon.options?.find(
                          (o) => o.id === optionId
                        );
                        return option;
                      })
                      .filter(Boolean);

                    // If no valid options found, clear invalid selections
                    if (selectedOptionDetails.length === 0) {
                      setTimeout(() => {
                        setAddonQuantities((prev) => {
                          const updated = { ...prev };
                          delete updated[addonId];
                          return updated;
                        });
                      }, 0);
                      return null;
                    }

                    return (
                      <div key={addonId} className="mb-2">
                        <h6 className="mb-1">{addon.title}</h6>
                        {selectedOptionDetails.map((option, badgeIndex) => {
                          const uniqueKey = `${addonId}-${option.id}-${badgeIndex}-badge`;
                          return (
                            <Badge
                              key={uniqueKey}
                              color="light"
                              className="me-1 mb-1 border"
                              style={{
                                fontSize: "0.85rem",
                                color: "black",
                                cursor: "pointer",
                              }}
                              onClick={() => handleChange(addon, option, true)}
                            >
                              {option.option}
                              {option.price
                                ? ` (+$${parseFloat(option.price).toFixed(2)})`
                                : ""}
                              <span
                                className="ms-1"
                                style={{ fontWeight: "bold" }}
                              >
                                ×
                              </span>
                            </Badge>
                          );
                        })}
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </ModalBody>

      <ModalFooter>
        <h5>Total: ${subtotal.toFixed(2)}</h5>
        <Button
          color="primary"
          onClick={handleAddToCart}
          disabled={subtotal <= 0}
        >
          Add to Cart (Alt+A)
        </Button>
        <Button color="danger" onClick={toggleModal}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default POSAddonModal;
